import type { ConversationEntry, Participant, TranscriptEntry } from '../store/conversationSlice';
import type { LandmineAlertRecord } from '../store/slices/landmineSlice';
import type { CompetitorAlertRecord } from '../store/slices/competitorSlice';
import type {
  SavedCompetitorRow,
  SavedLandmineRow,
  SavedParticipantRow,
  SavedQuestionRow,
  SavedTranscriptRow,
  SessionStateData,
} from '../types/sessionState';
import { ConversationCategory } from '../types/conversation';
import { HISTORY_MATCH_WINDOW_MS } from '../constants/sessionHistory';
import { parseServerTimestampMs } from './serverTimestamp';
import { cardKeyOf } from './cardAnswerPairing';

/**
 * History restore: the saved call (GET /api/sessions/:id/state) turned into
 * store rows marked `isRestored`, and the rules that keep it from duplicating
 * what the live sockets already delivered. Pure, so the matching is pinned by
 * tests (`sessionHistory.test.ts`).
 *
 * Cards carry no server id. A card's identity is the saver's own idempotency
 * key (`saver_service.py` `_push_once`): the question and its timestamp, a
 * landmine's trigger and detection time, a competitor's name and detection
 * time. The saver keeps the live frame's timestamp, so the two sides agree to
 * the millisecond the Mongo date keeps; HISTORY_MATCH_WINDOW_MS absorbs that.
 */

const normalizeText = (text?: string | null): string => (text ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Both timestamps parse and fall within the history match window. */
export const isWithinMatchWindow = (first?: string | null, second?: string | null): boolean => {
  const firstMs = parseServerTimestampMs(first);
  const secondMs = parseServerTimestampMs(second);
  return firstMs !== null && secondMs !== null && Math.abs(firstMs - secondMs) <= HISTORY_MATCH_WINDOW_MS;
};

type TranscriptIdentity = Pick<TranscriptEntry, 'text' | 'timestamp' | 'participantId'>;

/** Same line: participant, timestamp and text. A side without a participant id matches on the other two. */
export const isSameTranscriptLine = (first: TranscriptIdentity, second: TranscriptIdentity): boolean =>
  normalizeText(first.text) === normalizeText(second.text) &&
  isWithinMatchWindow(first.timestamp, second.timestamp) &&
  (!first.participantId || !second.participantId || first.participantId === second.participantId);

type QuestionIdentity = Pick<ConversationEntry, 'question' | 'timestamp'>;

export const isSameQuestionCard = (first: QuestionIdentity, second: QuestionIdentity): boolean =>
  normalizeText(first.question) === normalizeText(second.question) &&
  isWithinMatchWindow(first.timestamp, second.timestamp);

interface DetectionTime {
  detected_at?: string;
  timestamp?: string;
}

const detectionTimeOf = (alert: DetectionTime): string | undefined => alert.detected_at ?? alert.timestamp;

export const isSameLandmine = (first: DetectionTime & { trigger?: string }, second: DetectionTime & { trigger?: string }): boolean =>
  normalizeText(first.trigger) === normalizeText(second.trigger) &&
  isWithinMatchWindow(detectionTimeOf(first), detectionTimeOf(second));

export const isSameCompetitorAlert = (
  first: DetectionTime & { competitor?: string },
  second: DetectionTime & { competitor?: string }
): boolean =>
  normalizeText(first.competitor) === normalizeText(second.competitor) &&
  isWithinMatchWindow(detectionTimeOf(first), detectionTimeOf(second));

const compareTimes = (firstMs: number | null, secondMs: number | null): number => {
  if (firstMs === secondMs) return 0;
  if (firstMs === null) return 1;
  if (secondMs === null) return -1;
  return firstMs - secondMs;
};

/**
 * `incoming` merged into `existing` by time, existing rows keeping their
 * order: each incoming row goes before the first existing row that is later
 * than it, or at the end. A row with no readable time goes at the end.
 */
export function mergeByTime<T>(existing: T[], incoming: T[], getTimestamp: (row: T) => string | undefined): T[] {
  const timeOf = (row: T): number | null => parseServerTimestampMs(getTimestamp(row));
  const merged = [...existing];
  const sortedIncoming = [...incoming].sort((first, second) => compareTimes(timeOf(first), timeOf(second)));
  sortedIncoming.forEach((row) => {
    const rowMs = timeOf(row);
    const laterIndex = rowMs === null ? -1 : merged.findIndex((other) => (timeOf(other) ?? -Infinity) > rowMs);
    if (laterIndex === -1) merged.push(row);
    else merged.splice(laterIndex, 0, row);
  });
  return merged;
}

/** The rows of `incoming` that match none of `existing`. */
export const withoutDuplicates = <T>(incoming: T[], existing: T[], isSame: (first: T, second: T) => boolean): T[] =>
  incoming.filter((row) => !existing.some((other) => isSame(row, other)));

const toSpeaker = (speaker?: string | null): TranscriptEntry['speaker'] =>
  speaker === 'salesperson' ? 'salesperson' : 'customer';

const toParticipantId = (participantId?: string | number | null): string | undefined =>
  participantId !== undefined && participantId !== null && participantId !== '' ? String(participantId) : undefined;

/** Mongo persists recall ids as JSON numbers; the live roster keys on strings. */
export const toHistoryRoster = (rows?: SavedParticipantRow[]): Participant[] =>
  (rows ?? [])
    .filter((row) => toParticipantId(row?.participantId))
    .map((row) => ({
      participantId: toParticipantId(row.participantId) as string,
      name: row.name ?? undefined,
      role: row.role === 'salesperson' ? 'salesperson' : 'customer',
      source: row.source ?? undefined,
      externalId: toParticipantId(row.externalId),
    }));

export const toHistoryTranscripts = (rows?: SavedTranscriptRow[]): TranscriptEntry[] =>
  (rows ?? [])
    .filter((row) => row?.text?.trim() && row?.timestamp)
    .map((row, index) => ({
      id: row.id ? `history-${row.id}` : `history-${row.timestamp}-${index}`,
      text: row.text as string,
      speaker: toSpeaker(row.speaker),
      participantId: toParticipantId(row.participantId),
      speakerName: row.speakerName || undefined,
      timestamp: row.timestamp as string,
      isRestored: true,
    }));

const toHistoryEntry = (row: SavedQuestionRow, bucketCategory: string, index: number): ConversationEntry => {
  const isAnswered = row.status === 'completed' && !!row.answer;
  return {
    id: `history-${bucketCategory}-${row.timestamp}-${index}`,
    question: row.question as string,
    category: row.category || bucketCategory,
    relevance_score: typeof row.relevance_score === 'number' ? row.relevance_score : 0,
    timestamp: row.timestamp as string,
    status: isAnswered ? 'completed' : 'pending',
    stored_answer: row.stored_answer || undefined,
    answer: isAnswered ? (row.answer as string) : undefined,
    feedback: row.feedback?.rating ? row.feedback : undefined,
    participantId: toParticipantId(row.participantId),
    speakerName: row.speakerName || undefined,
    // A card restored while still waiting takes its live answers by the saved key.
    cardKey: cardKeyOf(row) ?? undefined,
    isNew: false,
    isRestored: true,
  };
};

const toBucketEntries = (rows: SavedQuestionRow[] | undefined, bucketCategory: string): ConversationEntry[] =>
  (rows ?? [])
    // `silent` rows went to the knowledge base and were never shown live.
    .filter((row) => row?.question?.trim() && row?.timestamp && !row?.silent)
    .map((row, index) => toHistoryEntry(row, bucketCategory, index));

/** Objections, FAQs and new inquiries as one time-ordered list of cards. */
export const toHistoryEntries = (state: SessionStateData): ConversationEntry[] =>
  mergeByTime(
    [],
    [
      ...toBucketEntries(state.detectedObjections, ConversationCategory.OBJECTION),
      ...toBucketEntries(state.detectedFAQs, ConversationCategory.FAQ),
      ...toBucketEntries(state.newInquiries, ConversationCategory.NEW_INQUIRY),
    ],
    (entry) => entry.timestamp
  );

export const toHistoryLandmines = (rows?: SavedLandmineRow[]): LandmineAlertRecord[] =>
  (rows ?? [])
    .filter((row) => row?.trigger && (row?.detected_at || row?.timestamp))
    .map((row) => ({
      ...row,
      trigger: row.trigger as string,
      matched_text: row.matched_text ?? '',
      matched_phrase: row.matched_phrase ?? '',
      similarity_score: row.similarity_score ?? 0,
      semantic_match: row.semantic_match ?? true,
      landmine_type: row.landmine_type || 'legal_risk',
      category: row.category ?? '',
      priority: row.priority ?? 'medium',
      can_say: row.can_say ?? [],
      cannot_say: row.cannot_say ?? [],
      detected_at: (row.detected_at ?? row.timestamp) as string,
      speaker: row.speaker ?? '',
      participantId: toParticipantId(row.participantId),
      speakerName: row.speakerName || undefined,
      isRestored: true,
    }));

export const toHistoryCompetitorAlerts = (rows?: SavedCompetitorRow[]): CompetitorAlertRecord[] =>
  (rows ?? [])
    .filter((row) => row?.competitor && (row?.detected_at || row?.timestamp))
    .map((row) => ({
      ...row,
      competitor: row.competitor as string,
      matched_text: row.matched_text ?? '',
      confidence: row.confidence ?? 0,
      match_type: row.match_type ?? '',
      advantages: row.advantages ?? [],
      weaknesses: row.weaknesses ?? [],
      talking_points: row.talking_points ?? '',
      category: row.category ?? '',
      detected_at: (row.detected_at ?? row.timestamp) as string,
      speaker: row.speaker ?? '',
      participantId: toParticipantId(row.participantId),
      speakerName: row.speakerName || undefined,
      isRestored: true,
    }));
